import "./HomeStyle.css";

import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Home = () => {
  const { user } = useAuth();

  return (
    <div className="doc-converter-app">
      {/* Hero Section */}
      <section className="hero-section gradient-bg text-white py-5">
        <div className="container py-5">
          <div className="row align-items-center min-vh-80">
            <div className="col-lg-6 mb-4 mb-lg-0 fade-in-up">
              <h1 className="display-4 fw-bold mb-4">
                Transform Your Documents
                <span className="d-block text-light opacity-75 mt-2">
                  With Professional Precision
                </span>
              </h1>
              <p className="lead mb-4 fs-5 opacity-90">
                Convert PDF to DOCX and DOCX to PDF seamlessly. Enterprise-grade
                security, lightning-fast processing, and perfect formatting
                every time.
              </p>
              <div className="d-flex flex-wrap gap-3 mb-4">
                {user ? (
                  <Link
                    to="/dashboard"
                    className="btn btn-light btn-lg px-4 py-3 fw-semibold rounded-pill shadow-lg"
                  >
                    <i className="fa fa-dashboard me-2"></i>
                    Go to Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/register"
                      className="btn btn-light btn-lg px-4 py-3 fw-semibold rounded-pill shadow-lg hover-lift"
                    >
                      <i className="fa fa-rocket me-2"></i>
                      Start Free Trial
                    </Link>
                    <Link
                      to="/login"
                      className="btn btn-outline-light btn-lg px-4 py-3 fw-semibold rounded-pill hover-lift"
                    >
                      <i className="fa fa-sign-in me-2"></i>
                      Sign In
                    </Link>
                  </>
                )}
              </div>
              <div className="d-flex align-items-center gap-4 flex-wrap text-light opacity-90">
                <div className="d-flex align-items-center">
                  <i className="fa fa-check-circle me-2 text-light"></i>
                  <span>No Credit Card Required</span>
                </div>
                <div className="d-flex align-items-center">
                  <i className="fa fa-check-circle me-2 text-light"></i>
                  <span>Free Trial Available</span>
                </div>
                <div className="d-flex align-items-center">
                  <i className="fa fa-check-circle me-2 text-light"></i>
                  <span>Secure & Encrypted</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6 text-center fade-in-up">
              <div className="position-relative">
                <div className="floating-card bg-white bg-opacity-10 p-4 p-lg-5 rounded-4 shadow-lg">
                  <div className="conversion-visual">
                    <div className="file-type pdf-type">
                      <i className="fa fa-file-pdf-o fa-4x text-danger mb-3"></i>
                      <div className="file-label">PDF</div>
                    </div>
                    <div className="conversion-arrow">
                      <i className="fa fa-exchange fa-2x text-white opacity-75"></i>
                    </div>
                    <div className="file-type word-type">
                      <i className="fa fa-file-word-o fa-4x text-primary mb-3"></i>
                      <div className="file-label">DOCX</div>
                    </div>
                  </div>
                </div>
                <div className="floating-element element-1"></div>
                <div className="floating-element element-2"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section py-5 bg-white">
        <div className="container py-5">
          <div className="text-center mb-5 fade-in-up">
            <h2 className="display-5 fw-bold mb-3">
              Why Choose <span className="gradient-text">Our Platform</span>?
            </h2>
            <p className="text-muted fs-5">
              Enterprise-grade document conversion with unmatched quality
            </p>
          </div>

          <div className="row g-4">
            {[
              {
                icon: "fa-bolt",
                title: "Lightning Fast",
                desc: "Convert documents in seconds with our optimized engine. No waiting, just instant results.",
                color: "primary",
              },
              {
                icon: "fa-shield",
                title: "Bank-Level Security",
                desc: "Military-grade encryption and automatic file deletion. Your privacy is guaranteed.",
                color: "success",
              },
              {
                icon: "fa-diamond",
                title: "Perfect Quality",
                desc: "Preserve all formatting, fonts, and layouts with pixel-perfect conversion accuracy.",
                color: "warning",
              },
              {
                icon: "fa-cloud",
                title: "Cloud Native",
                desc: "Access from any device, anywhere. Zero installation required with instant access.",
                color: "info",
              },
              {
                icon: "fa-refresh",
                title: "Batch Processing",
                desc: "Convert multiple documents simultaneously. Save hours with intelligent bulk operations.",
                color: "primary",
              },
              {
                icon: "fa-headphones",
                title: "24/7 Support",
                desc: "Round-the-clock expert support. Get help whenever you need it, day or night.",
                color: "success",
              },
            ].map((feature, index) => (
              <div key={index} className="col-md-6 col-lg-4 slide-in-left">
                <div className="feature-card bg-light rounded-4 p-4 h-100 shadow-sm hover-lift">
                  <div className={`feature-icon icon-${feature.color} mb-3`}>
                    <i className={`fa ${feature.icon}`}></i>
                  </div>
                  <h4 className="fw-bold mb-3">{feature.title}</h4>
                  <p className="text-muted mb-0">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="process-section py-5 bg-light">
        <div className="container py-5">
          <div className="text-center mb-5 fade-in-up">
            <h2 className="display-5 fw-bold mb-3">
              How It <span className="gradient-text">Works</span>
            </h2>
            <p className="text-muted fs-5">
              Simple three-step process for perfect conversions
            </p>
          </div>

          <div className="row g-5">
            {[
              {
                step: "1",
                title: "Upload Your File",
                desc: "Drag and drop or click to upload your PDF or DOCX file securely",
              },
              {
                step: "2",
                title: "AI-Powered Conversion",
                desc: "Our intelligent engine converts with perfect formatting preservation",
              },
              {
                step: "3",
                title: "Download Instantly",
                desc: "Get your converted file immediately with guaranteed quality",
              },
            ].map((process, index) => (
              <div key={index} className="col-md-4 text-center fade-in-up">
                <div className="process-step position-relative">
                  <div className="step-number gradient-bg text-white rounded-circle d-inline-flex align-items-center justify-content-center shadow-lg">
                    <span className="fs-4 fw-bold">{process.step}</span>
                  </div>
                  {index < 2 && (
                    <div className="step-connector d-none d-md-block"></div>
                  )}
                </div>
                <h4 className="fw-bold mb-3 mt-4">{process.title}</h4>
                <p className="text-muted">{process.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section py-5 bg-white">
        <div className="container py-5">
          <div className="row text-center">
            {[
              { number: "1M+", label: "Files Converted", color: "primary" },
              { number: "25K+", label: "Happy Users", color: "success" },
              { number: "99.9%", label: "Uptime SLA", color: "warning" },
              { number: "<2s", label: "Avg. Conversion", color: "info" },
            ].map((stat, index) => (
              <div key={index} className="col-6 col-md-3 mb-4 fade-in-up">
                <div className="stat-card">
                  <h2 className={`display-6 fw-bold text-${stat.color} mb-2`}>
                    {stat.number}
                  </h2>
                  <p className="text-muted mb-0 fw-medium">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section gradient-bg text-white py-5">
        <div className="container py-5 text-center">
          <div className="row justify-content-center">
            <div className="col-lg-8 fade-in-up">
              <h2 className="display-5 fw-bold mb-4">
                Ready to Transform Your Documents?
              </h2>
              <p className="lead mb-5 fs-5 opacity-90">
                Join 25,000+ professionals who trust us with their critical
                document conversions
              </p>
              <div className="d-flex flex-wrap gap-3 justify-content-center">
                {!user ? (
                  <Link
                    to="/register"
                    className="btn btn-light btn-lg px-5 py-3 fw-semibold rounded-pill shadow-lg hover-lift"
                  >
                    <i className="fa fa-rocket me-2"></i>
                    Start Free Trial
                  </Link>
                ) : (
                  <Link
                    to="/dashboard"
                    className="btn btn-light btn-lg px-5 py-3 fw-semibold rounded-pill shadow-lg hover-lift"
                  >
                    <i className="fa fa-dashboard me-2"></i>
                    Go to Dashboard
                  </Link>
                )}
                <Link
                  to="/features"
                  className="btn btn-outline-light btn-lg px-5 py-3 fw-semibold rounded-pill hover-lift"
                >
                  <i className="fa fa-play-circle me-2"></i>
                  See Demo
                </Link>
              </div>
              <p className="mt-4 text-light opacity-90">
                <i className="fa fa-check-circle me-2 text-light"></i>
                No credit card required • Free trial available • Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
